import { ReactNode } from 'react';
import { MAIN_PACE_TASK_IDs } from '@symfa-inc/providence-types';
import { ColumnsType, ColumnType } from 'antd/lib/table';
import {
  priceCommaReplacer,
  projectCategoryRender,
  projectPaceTaskDataRender,
  providenceAgentRender,
  readOnlyDatePickerRenderer,
  readOnlyInputNumberRender,
} from '@core/utils/methods';
import { actionButtons } from '@models/constants';
import {
  AccountingBrowse,
  IdAndValue,
  OOMInformation,
  SiteBrowse,
} from '@models/interfaces';
import { Browse, ColumnsFunc } from '@models/types';

export const getAccountingColumns = (
  editFunc?: ColumnsFunc<AccountingBrowse>,
): ColumnsType<Browse> => [
  {
    title: 'PACE Project',
    dataIndex: 'categories',
    key: 'PACEProjectNumber',
    width: 150,
    sorter: true,
    render: projectCategoryRender({
      field: 'PACEProjectNumber',
    }),
  },
  {
    title: 'IWM Job Number',
    dataIndex: 'categories',
    key: 'IWMJobNumber',
    width: 170,
    sorter: true,
    render: projectCategoryRender({
      field: 'IWMJobNumber',
    }),
  },
  {
    title: 'Oracle Project',
    dataIndex: 'categories',
    key: 'oracleProjectNumber',
    width: 150,
    sorter: true,
    render: projectCategoryRender({
      field: 'oracleProjectNumber',
    }),
  },
  {
    title: 'Project Category',
    dataIndex: 'categories',
    key: 'projectCategory',
    width: 190,
    sorter: true,
    render: projectCategoryRender({
      field: 'projectCategory',
    }),
  },
  {
    title: 'FA Location',
    dataIndex: 'site',
    key: 'FALocationNumber',
    width: 130,
    sorter: true,
    render: (site: SiteBrowse): ReactNode =>
      readOnlyInputNumberRender(site?.FALocationNumber),
  },
  {
    title: 'Site Number',
    dataIndex: 'site',
    key: 'siteNumber',
    width: 140,
    sorter: true,
    render: (site: SiteBrowse): ReactNode => site?.siteNumber,
  },
  {
    title: 'Legacy Site ID',
    width: 150,
    key: 'legacySiteID',
    sorter: true,
    dataIndex: 'site',
    render: (site: SiteBrowse): ReactNode => site.legacySiteID,
  },
  {
    title: 'Alternate Site Info',
    width: 170,
    key: 'alternateSiteInfo',
    sorter: true,
    dataIndex: 'site',
    render: (site: SiteBrowse): ReactNode => site.alternateSiteInfo,
  },
  {
    title: 'USID',
    width: 150,
    key: 'USID',
    sorter: true,
    dataIndex: 'site',
    render: (site: SiteBrowse): ReactNode => site.USID,
  },
  {
    title: 'Submarket',
    key: 'submarket',
    width: 140,
    sorter: true,
    dataIndex: 'site',
    render: (site: SiteBrowse): ReactNode => site.submarket?.value,
  },
  {
    title: 'Providence Agent',
    key: 'providenceAgent',
    width: 180,
    sorter: true,
    dataIndex: 'providenceAgent',
    render: providenceAgentRender,
  },
  {
    title: 'Tower Owner',
    key: 'towerOwner',
    width: 145,
    dataIndex: 'site',
    sorter: true,
    render: (site: SiteBrowse): ReactNode => site?.towerOwner?.value,
  },
  {
    title: 'Project Type',
    dataIndex: 'projectType',
    key: 'projectType',
    width: 140,
    sorter: true,
    render: (projectType: IdAndValue): ReactNode => projectType?.value,
  },
  {
    title: 'Project Work Type',
    dataIndex: 'projectWorkType',
    key: 'projectWorkType',
    width: 180,
    sorter: true,
    render: (projectWorkType: IdAndValue): ReactNode => projectWorkType?.value,
  },
  {
    title: 'SS010 Actual',
    key: 'SS010',
    dataIndex: 'projectPaceTaskData',
    width: 140,
    render: projectPaceTaskDataRender({
      id: MAIN_PACE_TASK_IDs.candidateApproved,
      field: 'actualDate',
    }),
  },
  {
    title: 'SS010 Invoice Number',
    key: 'SS010InvoiceNumber',
    dataIndex: 'projectPaceTaskData',
    width: 190,
    render: projectPaceTaskDataRender({
      id: MAIN_PACE_TASK_IDs.candidateApproved,
      field: 'invoiceNumber',
    }),
  },
  {
    title: 'SS010 Invoice Date',
    key: 'SS010InvoiceDate',
    dataIndex: 'projectPaceTaskData',
    width: 170,
    render: projectPaceTaskDataRender({
      id: MAIN_PACE_TASK_IDs.candidateApproved,
      field: 'invoiceDate',
    }),
  },
  {
    title: 'RE066 Actual',
    key: 'RE066',
    dataIndex: 'projectPaceTaskData',
    width: 140,
    render: projectPaceTaskDataRender({
      id: MAIN_PACE_TASK_IDs.zoningAppDeemedComplete,
      field: 'actualDate',
    }),
  },
  {
    title: 'RE066 Invoice Number',
    key: 'RE066InvoiceNumber',
    dataIndex: 'projectPaceTaskData',
    width: 190,
    render: projectPaceTaskDataRender({
      id: MAIN_PACE_TASK_IDs.zoningAppDeemedComplete,
      field: 'invoiceNumber',
    }),
  },
  {
    title: 'RE066 Invoice Date',
    key: 'RE066InvoiceDate',
    dataIndex: 'projectPaceTaskData',
    width: 170,
    render: projectPaceTaskDataRender({
      id: MAIN_PACE_TASK_IDs.zoningAppDeemedComplete,
      field: 'invoiceDate',
    }),
  },
  {
    title: 'RE015 Actual Date',
    key: 'RE015ActualDate',
    dataIndex: 'projectPaceTaskData',
    width: 160,
    render: projectPaceTaskDataRender({
      id: MAIN_PACE_TASK_IDs.leasingComplete,
      field: 'actualDate',
    }),
  },
  {
    title: 'RE015 OOM Amount',
    key: 'RE015OOMAmount',
    dataIndex: 'OOMInformationData',
    width: 190,
    render: (value: Record<string, OOMInformation>): ReactNode => {
      const currenValue = value[MAIN_PACE_TASK_IDs.leasingComplete]?.OOMAmount;

      return currenValue ? `$ ${priceCommaReplacer(currenValue)}` : currenValue;
    },
  },
  {
    title: 'RE015 OOM Date',
    key: 'RE015OOMDate',
    dataIndex: 'OOMInformationData',
    width: 190,
    render: (value: Record<string, OOMInformation>): ReactNode => {
      const currentValue = value[MAIN_PACE_TASK_IDs.leasingComplete]?.OOMDate;

      return currentValue
        ? readOnlyDatePickerRenderer(currentValue)
        : currentValue;
    },
  },
  {
    title: 'RE015 OOM Total Amount',
    key: 'RE015OOMTotalAmount',
    dataIndex: 'OOMInformationData',
    width: 210,
    render: (value: Record<string, OOMInformation>): ReactNode => {
      const currenValue =
        value[MAIN_PACE_TASK_IDs.leasingComplete]?.OOMTotalAmount;

      return currenValue ? `$ ${priceCommaReplacer(currenValue)}` : currenValue;
    },
  },
  {
    title: 'RE015 Invoice Number',
    key: 'RE015InvoiceNumber',
    dataIndex: 'projectPaceTaskData',
    width: 190,
    render: projectPaceTaskDataRender({
      id: MAIN_PACE_TASK_IDs.leasingComplete,
      field: 'invoiceNumber',
    }),
  },
  {
    title: 'RE015 Invoice Date',
    key: 'RE015InvoiceDate',
    dataIndex: 'projectPaceTaskData',
    width: 170,
    render: projectPaceTaskDataRender({
      id: MAIN_PACE_TASK_IDs.leasingComplete,
      field: 'invoiceDate',
    }),
  },
  {
    title: 'RE020 Actual Date',
    key: 'RE020SiteAcqCompleteActual',
    dataIndex: 'projectPaceTaskData',
    width: 190,
    render: projectPaceTaskDataRender({
      id: MAIN_PACE_TASK_IDs.siteAcqComplete,
      field: 'actualDate',
    }),
  },
  {
    title: 'RE020 OOM Amount',
    key: 'RE020OOMAmount',
    dataIndex: 'OOMInformationData',
    width: 190,
    render: (value: Record<string, OOMInformation>): ReactNode => {
      const currenValue = value[MAIN_PACE_TASK_IDs.siteAcqComplete]?.OOMAmount;

      return currenValue ? `$ ${priceCommaReplacer(currenValue)}` : currenValue;
    },
  },
  {
    title: 'RE020 OOM Date',
    key: 'RE020OOMDate',
    dataIndex: 'OOMInformationData',
    width: 190,
    render: (value: Record<string, OOMInformation>): ReactNode => {
      const currentValue = value[MAIN_PACE_TASK_IDs.siteAcqComplete]?.OOMDate;

      return currentValue
        ? readOnlyDatePickerRenderer(currentValue)
        : currentValue;
    },
  },
  {
    title: 'RE020 OOM Total Amount',
    key: 'RE020OOMTotalAmount',
    dataIndex: 'OOMInformationData',
    width: 210,
    render: (value: Record<string, OOMInformation>): ReactNode => {
      const currenValue =
        value[MAIN_PACE_TASK_IDs.siteAcqComplete]?.OOMTotalAmount;

      return currenValue ? `$ ${priceCommaReplacer(currenValue)}` : currenValue;
    },
  },
  {
    title: 'RE020 Invoice Number',
    key: 'siteAcqCompleteRE020InvoiceNumber',
    dataIndex: 'projectPaceTaskData',
    width: 190,
    render: projectPaceTaskDataRender({
      id: MAIN_PACE_TASK_IDs.siteAcqComplete,
      field: 'invoiceNumber',
    }),
  },
  {
    title: 'RE020 Invoice Date',
    key: 'siteAcqCompleteRE020InvoiceDate',
    dataIndex: 'projectPaceTaskData',
    width: 200,
    render: projectPaceTaskDataRender({
      id: MAIN_PACE_TASK_IDs.siteAcqComplete,
      field: 'invoiceDate',
    }),
  },
  {
    title: 'RE097 PACE Actual Date',
    key: 'RE097',
    dataIndex: 'projectPaceTaskData',
    width: 200,
    render: projectPaceTaskDataRender({
      id: MAIN_PACE_TASK_IDs.OOMTruedUp,
      field: 'PACEActualDate',
    }),
  },
  {
    title: 'RE060 PACE Actual Date',
    key: 'RE060',
    dataIndex: 'projectPaceTaskData',
    width: 200,
    render: projectPaceTaskDataRender({
      id: MAIN_PACE_TASK_IDs.finalSiteAcqAccp,
      field: 'PACEActualDate',
    }),
  },
  { ...actionButtons({ editFunc }), fixed: 'right' } as ColumnType<Browse>,
];
